import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { WalletLink } from "./WalletLink";
import { QUARTER_PRESETS } from "./QuarterSelector";
import { Skeleton } from "./Skeleton";

interface ReferralContributionsTableProps {
  walletAddress: string;
}

type WindowId = "7d" | "30d" | "quarter";

const WINDOWS: { id: WindowId; label: string }[] = [
  { id: "7d", label: "Last 7d" },
  { id: "30d", label: "Last 30d" },
  { id: "quarter", label: "Current quarter" },
];

function rangeFor(id: WindowId): { from: string; to: string; label: string } {
  const now = new Date();
  if (id === "quarter") {
    const q = QUARTER_PRESETS[0]!;
    return { from: q.from, to: q.to, label: q.label };
  }
  const days = id === "7d" ? 7 : 30;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString(),
    to: now.toISOString(),
    label: id === "7d" ? "Last 7 days" : "Last 30 days",
  };
}

export function ReferralContributionsTable({
  walletAddress,
}: ReferralContributionsTableProps) {
  const [windowId, setWindowId] = useState<WindowId>("7d");
  const range = useMemo(() => rangeFor(windowId), [windowId]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "referral-contributions",
      walletAddress,
      range.from,
      range.to,
    ],
    queryFn: async () => {
      const { data, error } = await api
        .wallet({ walletAddress })
        .referralContributions.get({
          query: { from: range.from, to: range.to },
        });
      if (error) throw error;
      return data;
    },
    enabled: !!walletAddress,
  });

  const rows = data?.data ?? [];
  const withGain = rows.filter((r) => r.pointsGained > 0);
  const totalGain = data?.totalReferralGain ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="seg">
          {WINDOWS.map((w) => (
            <button
              key={w.id}
              onClick={() => setWindowId(w.id)}
              className={`seg-btn ${windowId === w.id ? "seg-btn-on" : ""}`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 ml-auto">{range.label}</span>
      </div>

      {isLoading && (
        <Skeleton kind="table" rows={4} />
      )}
      {error && (
        <p className="text-red-400 text-sm">Could not load contributions.</p>
      )}

      {data && !isLoading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="card-inset p-3">
              <div className="text-gray-400 text-xs mb-1">
                Referrals with gain
              </div>
              <div className="text-lg font-semibold text-gray-50 num">
                {withGain.length}/{rows.length}
              </div>
            </div>
            <div className="card-inset p-3">
              <div className="text-gray-400 text-xs mb-1">
                Total points by referrals
              </div>
              <div className="text-lg font-semibold text-gray-50 num">
                +{totalGain.toLocaleString()}
              </div>
            </div>
            <div className="card-inset p-3">
              <div className="text-gray-400 text-xs mb-1">
                Your est. share (10%)
              </div>
              <div className="text-lg font-semibold text-yellow-400 num">
                +{Math.round(totalGain * 0.1).toLocaleString()}
              </div>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No referred wallets found for this wallet.
            </p>
          ) : withGain.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No referrals gained points in this window.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-2 pr-2 font-medium">#</th>
                    <th className="pb-2 pr-2 font-medium">Wallet</th>
                    <th className="pb-2 pr-2 font-medium">Refcode</th>
                    <th className="pb-2 pr-2 text-right font-medium">Gained</th>
                    <th className="pb-2 pr-2 text-right font-medium">
                      Your share (~10%)
                    </th>
                    <th className="pb-2 pr-2 text-right font-medium">Primary</th>
                    <th className="pb-2 pr-2 text-right font-medium">Secondary</th>
                    <th className="pb-2 text-right font-medium">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {withGain.map((r, i) => (
                    <tr key={r.walletAddress} className="table-row">
                      <td className="py-2 pr-2 text-gray-500 num">{i + 1}</td>
                      <td className="py-2 pr-2">
                        <WalletLink address={r.walletAddress} />
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs text-gray-300">
                        {r.refcode ?? "-"}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-50 font-medium num">
                        +{r.pointsGained.toLocaleString()}
                      </td>
                      <td className="py-2 pr-2 text-right text-yellow-400 num">
                        +{Math.round(r.pointsGained * 0.1).toLocaleString()}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-300 num">
                        +{r.primaryGained.toLocaleString()}
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-300 num">
                        +{r.secondaryGained.toLocaleString()}
                      </td>
                      <td className="py-2 text-right text-gray-300 num">
                        +{r.bonusGained.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-gray-500">
            Share estimate assumes ~1 point per 10 points your referral earns.
            Adjust if the actual ratio is different.
          </p>
        </>
      )}
    </div>
  );
}
